/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react'
import {Snackbar, Typography} from '@mui/material'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {
  buildSecondaryAssumptionResponseBody,
  formatAssumptionDateTime,
  getInitialResponseForm,
  isResponseFormValid,
  isSecondaryAssumptionAnswered,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm as ResponseForm
} from 'src/constants/secondaryAssumptions'
import AssumptionResponseForm from './AssumptionResponseForm'

// Legacy-stack twin of pages-new/secondaryAssumptions/AssumptionResponseDialogNew.

type AssumptionResponseDialogProps = {
  open: boolean
  assumption?: SecondaryAssumption
  onClose: () => void
  // Carries the document as the server actually persisted it (state/takenAt/bodyPart/respondedAt),
  // so the caller can patch its own list in place instead of refetching the whole window.
  onSuccess: (updated?: SecondaryAssumption) => void
  // Opened from the list's round ✓ button: "taken" is already answered by that tap, so the form
  // starts on "yes" and skips the taken question — only date/time (defaulted to the expected one)
  // and the body part remain to ask. Never set together with an already-answered assumption.
  presetTaken?: 'yes'
}

const AssumptionResponseDialog: React.FC<AssumptionResponseDialogProps> = ({
  open,
  assumption,
  onClose,
  onSuccess,
  presetTaken
}) => {
  const [form, setForm] = useState<ResponseForm>(getInitialResponseForm())
  const [statusSubmit, setStatusSubmit] = useState<string | undefined>(undefined)

  // Reset on every open so a previously edited row never leaks its form into the next one.
  useEffect(() => {
    if (open) {
      setForm(
        presetTaken ?
          {taken: presetTaken, takenAt: assumption?.expectedDate ?? null, bodyPart: []} :
          getInitialResponseForm(assumption)
      )
      setStatusSubmit(undefined)
    }
  }, [open, assumption?._id])

  const onSubmit = async () => {
    if (!assumption) { return }
    const body = buildSecondaryAssumptionResponseBody(form)
    // RF4.3: the derived "next dose" row (no _id yet) is addressed by natural key instead.
    const updated = assumption._id
      ? await apiClient.patchSecondaryAssumption(assumption._id, body, setStatusSubmit)
      : await apiClient.patchSecondaryAssumptionByKey(
        {secondaryTherapyId: assumption.secondaryTherapyId, expectedDate: assumption.expectedDate as string},
        body,
        setStatusSubmit
      )
    if (updated) {
      onSuccess(updated)
    }
  }

  const wasAlreadyAnswered = isSecondaryAssumptionAnswered(assumption)

  return (
    <>
      <DialogCustom
        maxWidth='sm'
        onClose={onClose}
        open={open}
        title={{text: wasAlreadyAnswered ? 'Modifica risposta' : 'Conferma assunzione', format: false}}
        body={assumption ? (
          <>
            <Typography variant='body2' color='textSecondary' style={{marginBottom: 24}}>
              {`${assumption.drugName ?? 'Terapia accessoria'} — assunzione prevista il ${formatAssumptionDateTime(assumption.expectedDate)}.`}
            </Typography>
            <AssumptionResponseForm
              assumption={assumption}
              form={form}
              onChange={setForm}
              disabled={statusSubmit === STATUS.PENDING}
              hideTakenQuestion={Boolean(presetTaken)}
            />
          </>
        ) : <></>}
        footer={
          <>
            <SecondaryButton onClick={onClose}>Annulla</SecondaryButton>
            <PrimaryButton
              disabled={!isResponseFormValid(form) || statusSubmit === STATUS.PENDING}
              onClick={onSubmit}
            >
              {presetTaken ? 'Conferma' : (wasAlreadyAnswered ? 'Aggiorna' : 'Invia')}
            </PrimaryButton>
          </>
        }
      />

      <Snackbar
        open={statusSubmit === STATUS.REJECTED}
        autoHideDuration={4000}
        onClose={() => setStatusSubmit(undefined)}
      >
        <SnackbarAlertCustom severity='error' onClose={() => setStatusSubmit(undefined)}>
          Impossibile registrare la risposta. Riprova.
        </SnackbarAlertCustom>
      </Snackbar>
    </>
  )
}

export default AssumptionResponseDialog
